import { useEffect, useMemo, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import { BookingPricingType, type GuideDaySchedule, type GuidePricing } from '@/types';
import { formatGuidePrice } from '@/features/guide/utils/pricing';
import {
  addDays,
  daysBetweenInclusive,
  formatClockTime,
  fromMinutes,
  getDurationOptions,
  getStartSlots,
  isTourAvailable,
  maxFullDayLength,
  toMinutes,
  type ScheduleMeta,
  type StartSlot,
} from '@/features/booking/utils/timeSlots';

export interface TripWhen {
  travelDate: string;
  /** Full day: last day, inclusive. Set by the days stepper, never picked directly. */
  endDate: string;
  startTime: string;
  endTime: string;
}

interface TripWhenPickerProps {
  pricingTypes: BookingPricingType[];
  pricingType: BookingPricingType | '';
  onPricingTypeChange: (type: BookingPricingType) => void;
  value: TripWhen;
  onChange: (next: TripWhen) => void;
  meta?: ScheduleMeta;
  /** Rates shown on the option cards. */
  pricing?: Pick<GuidePricing, 'hourlyPrice' | 'fullDayPrice' | 'currency'> | null;
  days: Map<string, GuideDaySchedule>;
  /** Called with "YYYY-MM" whenever a calendar shows a new month, so its schedule loads. */
  onMonthVisible: (month: string) => void;
}

const DATE_FORMAT = 'ddd, D MMM YYYY';
const SHORT_DATE_FORMAT = 'ddd, D MMM';
const DEFAULT_HOURS = 2;
/** Chips at or after noon go under "Afternoon". */
const NOON = 12 * 60;

function toDayjs(date: string) {
  return date ? dayjs(date) : null;
}

function formatDay(date: string) {
  return dayjs(date).format(SHORT_DATE_FORMAT);
}

function hoursLabel(hours: number) {
  return hours === 1 ? '1 hour' : `${hours} hours`;
}

function daysLabel(days: number) {
  return days === 1 ? '1 day' : `${days} days`;
}

/** Whether a trip of this type can start on `day`. Days not loaded yet pass, until they load. */
export function isDayBookableFor(type: BookingPricingType, day: GuideDaySchedule | undefined) {
  if (!day) return true;
  return type === BookingPricingType.FULL_DAY
    ? day.status === 'free'
    : day.status === 'free' || day.status === 'partial';
}

function Step({ number, title, children }: { number: number; title: string; children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      <Box
        sx={{
          width: 24,
          height: 24,
          flexShrink: 0,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'grid',
          placeItems: 'center',
          fontSize: '0.75rem',
          fontWeight: 800,
        }}
      >
        {number}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} sx={{ mb: 1, lineHeight: '24px' }}>
          {title}
        </Typography>
        {children}
      </Box>
    </Box>
  );
}

function Stepper({
  value,
  label,
  min,
  max,
  unit,
  onChange,
}: {
  value: number;
  label: string;
  min: number;
  max: number;
  /** Plural noun for the button labels, e.g. "hours". */
  unit: string;
  onChange: (next: number) => void;
}) {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
      <IconButton
        size="small"
        aria-label={`Fewer ${unit}`}
        onClick={() => onChange(value - 1)}
        disabled={value <= min}
        sx={{ border: '1px solid', borderColor: 'divider' }}
      >
        <RemoveIcon fontSize="small" />
      </IconButton>
      <Typography fontWeight={800} sx={{ minWidth: 72, textAlign: 'center' }} aria-live="polite">
        {label}
      </Typography>
      <IconButton
        size="small"
        aria-label={`More ${unit}`}
        onClick={() => onChange(value + 1)}
        disabled={value >= max}
        sx={{ border: '1px solid', borderColor: 'divider' }}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

function SlotGroup({
  title,
  slots,
  selected,
  onSelect,
}: {
  title: string;
  slots: StartSlot[];
  selected: number | null;
  onSelect: (start: number) => void;
}) {
  if (slots.length === 0) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
        {title}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(4, 1fr)' },
          gap: 1,
        }}
      >
        {slots.map((slot) => {
          const isSelected = slot.start === selected;
          const label = formatClockTime(fromMinutes(slot.start));
          return (
            <Chip
              key={slot.start}
              label={label}
              clickable={slot.available}
              disabled={!slot.available}
              color={isSelected ? 'primary' : 'default'}
              variant={isSelected ? 'filled' : 'outlined'}
              onClick={slot.available ? () => onSelect(slot.start) : undefined}
              aria-label={slot.available ? `Start at ${label}` : `${label}, already booked`}
              aria-pressed={isSelected}
              sx={{ fontWeight: 600 }}
            />
          );
        })}
      </Box>
    </Box>
  );
}

function Summary({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
        border: '1px solid',
        borderColor: 'success.light',
      }}
    >
      <CheckCircleIcon color="success" fontSize="small" />
      <Typography variant="body2" fontWeight={700}>
        {children}
      </Typography>
    </Box>
  );
}

export function TripWhenPicker({
  pricingTypes,
  pricingType,
  onPricingTypeChange,
  value,
  onChange,
  meta,
  pricing,
  days,
  onMonthVisible,
}: TripWhenPickerProps) {
  const selectedDay = days.get(value.travelDate);
  const isHourly = pricingType === BookingPricingType.HOURLY;
  const isFullDay = pricingType === BookingPricingType.FULL_DAY;
  const workingLabel = meta
    ? `${formatClockTime(meta.workingHours.start)} – ${formatClockTime(meta.workingHours.end)}`
    : '';

  const [chosenHours, setChosenHours] = useState(() =>
    value.startTime && value.endTime
      ? (toMinutes(value.endTime) - toMinutes(value.startTime)) / 60
      : DEFAULT_HOURS
  );

  // Longest tour that still fits somewhere on the chosen day (0 when the day is full).
  const maxHours = useMemo(() => {
    if (!meta || !selectedDay) return 0;
    const fitting = getDurationOptions(meta).filter((option) =>
      getStartSlots(selectedDay, meta, option).some((slot) => slot.available)
    );
    return fitting.length > 0 ? Math.max(...fitting) : 0;
  }, [meta, selectedDay]);
  const hours = Math.max(1, Math.min(chosenHours, maxHours || chosenHours));

  const startSlots = useMemo(
    () => (meta && isHourly && selectedDay ? getStartSlots(selectedDay, meta, hours) : []),
    [meta, isHourly, selectedDay, hours]
  );
  const selectedStart = value.startTime ? toMinutes(value.startTime) : null;

  // Full day: the trip length is carried by endDate and edited only through the days stepper.
  const dayCount =
    value.travelDate && value.endDate && value.endDate >= value.travelDate
      ? daysBetweenInclusive(value.travelDate, value.endDate)
      : 1;
  const maxDays = meta && value.travelDate ? maxFullDayLength(days, meta, value.travelDate) : 0;
  // A short run only means "booked from then" once the day after it has loaded.
  const maxDaysKnown =
    meta != null &&
    Boolean(value.travelDate) &&
    (maxDays === meta.maxFullDayDays || days.has(addDays(value.travelDate, maxDays)));

  // Shrink a trip that no longer fits (new start date, a range from search, a day booked meanwhile).
  useEffect(() => {
    if (!isFullDay || !maxDaysKnown || maxDays === 0 || dayCount <= maxDays) return;
    onChange({ ...value, endDate: addDays(value.travelDate, maxDays - 1) });
  }, [isFullDay, maxDaysKnown, maxDays, dayCount, value, onChange]);

  const setDate = (date: Dayjs | null) => {
    const next = date?.isValid() ? date.format('YYYY-MM-DD') : '';
    // A new day invalidates the chosen time; a full-day trip keeps its length (shrunk above if needed).
    onChange({
      travelDate: next,
      endDate: next ? addDays(next, dayCount - 1) : '',
      startTime: '',
      endTime: '',
    });
  };

  const setDays = (next: number) => {
    onChange({ ...value, endDate: addDays(value.travelDate, next - 1) });
  };

  const selectStart = (start: number) => {
    onChange({ ...value, startTime: fromMinutes(start), endTime: fromMinutes(start + hours * 60) });
  };

  /** Keep the chosen start when the new length still fits there; otherwise ask again. */
  const changeHours = (next: number) => {
    setChosenHours(next);
    if (selectedStart == null || !meta) return;
    const stillFits = isTourAvailable(selectedDay, meta, selectedStart, selectedStart + next * 60);
    onChange({
      ...value,
      startTime: stillFits ? value.startTime : '',
      endTime: stillFits ? fromMinutes(selectedStart + next * 60) : '',
    });
  };

  const isDayBlocked = (date: Dayjs) =>
    pricingType !== '' && !isDayBookableFor(pricingType, days.get(date.format('YYYY-MM-DD')));

  const hasBookedSlots = startSlots.some((slot) => !slot.available);
  const longestTour = meta ? getDurationOptions(meta).length : 0;
  const currency = pricing?.currency ?? 'INR';

  const options: Record<
    BookingPricingType,
    { title: string; subtitle: string; price: string | null; icon: ReactNode }
  > = {
    [BookingPricingType.HOURLY]: {
      title: 'Few hours',
      subtitle: longestTour > 1 ? `1–${longestTour} hrs · you pick the start` : 'You pick the start',
      price: pricing?.hourlyPrice ? `${formatGuidePrice(pricing.hourlyPrice, currency)} / hour` : null,
      icon: <ScheduleIcon fontSize="small" />,
    },
    [BookingPricingType.FULL_DAY]: {
      title: 'Full day',
      subtitle: workingLabel ? `${workingLabel} · one or more days` : 'One or more days',
      price: pricing?.fullDayPrice
        ? `${formatGuidePrice(pricing.fullDayPrice, currency)} / day`
        : null,
      icon: <WbSunnyOutlinedIcon fontSize="small" />,
    },
  };

  // A date carried over from search, or from the other option, may not suit this one.
  let unavailableNotice: string | null = null;
  if (value.travelDate && selectedDay && pricingType && !isDayBookableFor(pricingType, selectedDay)) {
    unavailableNotice =
      isFullDay &&
      selectedDay.status === 'partial' &&
      pricingTypes.includes(BookingPricingType.HOURLY)
        ? `The guide is only free for part of ${formatDay(value.travelDate)}. Pick another date, or switch to Few hours.`
        : `The guide isn't available on ${formatDay(value.travelDate)}. Please pick another date.`;
  }

  const renderDatePicker = (label: string) => (
    <DatePicker
      value={toDayjs(value.travelDate)}
      onChange={setDate}
      onMonthChange={(month) => onMonthVisible(month.format('YYYY-MM'))}
      shouldDisableDate={isDayBlocked}
      disablePast
      format={DATE_FORMAT}
      slotProps={{
        textField: {
          size: 'small',
          fullWidth: true,
          placeholder: 'Select a date',
          inputProps: { 'aria-label': label },
          helperText: 'Greyed-out dates are booked or unavailable',
        },
      }}
    />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <ToggleButtonGroup
        exclusive
        fullWidth
        color="primary"
        value={pricingType}
        onChange={(_, next: BookingPricingType | null) => next && onPricingTypeChange(next)}
        sx={{
          gap: 1.5,
          '& .MuiToggleButtonGroup-grouped': {
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '12px !important',
          },
        }}
      >
        {pricingTypes.map((type) => (
          <ToggleButton
            key={type}
            value={type}
            sx={{
              position: 'relative',
              flexDirection: 'column',
              alignItems: 'flex-start',
              textAlign: 'left',
              textTransform: 'none',
              justifyContent: 'flex-start',
              gap: 0.5,
              py: 2,
              pl: 2.5,
              // Room for the selected check icon so the title never runs under it.
              pr: 5,
              color: 'text.primary',
              '&.Mui-selected': {
                borderColor: (theme) => `${theme.palette.primary.main} !important`,
                boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.primary.main}`,
              },
            }}
          >
            {type === pricingType && (
              <CheckCircleIcon
                color="primary"
                fontSize="small"
                sx={{ position: 'absolute', top: 14, right: 14 }}
              />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'primary.main' }}>
              {options[type].icon}
              <Typography variant="body2" fontWeight={800} color="text.primary">
                {options[type].title}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {options[type].subtitle}
            </Typography>
            {options[type].price && (
              <Typography variant="body2" fontWeight={700} color="primary.main" sx={{ mt: 0.5 }}>
                {options[type].price}
              </Typography>
            )}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {isHourly && (
        <>
          <Step number={1} title="Choose a date">
            {renderDatePicker('Tour date')}
          </Step>

          {unavailableNotice && <Alert severity="warning">{unavailableNotice}</Alert>}

          {value.travelDate && selectedDay && !unavailableNotice && maxHours === 0 && (
            <Alert severity="warning">
              The guide is fully booked on {formatDay(value.travelDate)}. Please pick another date.
            </Alert>
          )}

          {value.travelDate && maxHours > 0 && (
            <>
              <Step number={2} title="How long?">
                <Stepper
                  value={hours}
                  label={hoursLabel(hours)}
                  min={1}
                  max={maxHours}
                  unit="hours"
                  onChange={changeHours}
                />
              </Step>

              <Step number={3} title="Start time">
                <SlotGroup
                  title="Morning"
                  slots={startSlots.filter((slot) => slot.start < NOON)}
                  selected={selectedStart}
                  onSelect={selectStart}
                />
                <SlotGroup
                  title="Afternoon"
                  slots={startSlots.filter((slot) => slot.start >= NOON)}
                  selected={selectedStart}
                  onSelect={selectStart}
                />
                {hasBookedSlots && (
                  <Typography variant="caption" color="text.secondary">
                    Greyed-out times are taken.
                  </Typography>
                )}
              </Step>

              {value.startTime && value.endTime && (
                <Summary>
                  {formatDay(value.travelDate)} · {formatClockTime(value.startTime)} –{' '}
                  {formatClockTime(value.endTime)} ({hoursLabel(hours)})
                </Summary>
              )}
            </>
          )}
        </>
      )}

      {isFullDay && (
        <>
          <Step number={1} title="Start date">
            {renderDatePicker('Start date')}
          </Step>

          {unavailableNotice && <Alert severity="warning">{unavailableNotice}</Alert>}

          {meta && value.travelDate && maxDays > 0 && (
            <Step number={2} title="How many days?">
              <Stepper
                value={Math.min(dayCount, maxDays)}
                label={daysLabel(Math.min(dayCount, maxDays))}
                min={1}
                max={maxDays}
                unit="days"
                onChange={setDays}
              />
              {maxDaysKnown && dayCount >= maxDays && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {maxDays === meta.maxFullDayDays
                    ? `Up to ${meta.maxFullDayDays} days per booking`
                    : `The guide is booked from ${formatDay(addDays(value.travelDate, maxDays))}`}
                </Typography>
              )}
            </Step>
          )}

          {value.travelDate && value.endDate && maxDays > 0 && dayCount <= maxDays && (
            <Summary>
              {dayCount === 1
                ? `${formatDay(value.travelDate)}${workingLabel ? ` · ${workingLabel}` : ''}`
                : `${formatDay(value.travelDate)} → ${formatDay(value.endDate)} · ${daysLabel(dayCount)}${
                    workingLabel ? ` · ${workingLabel} daily` : ''
                  }`}
            </Summary>
          )}
        </>
      )}
    </Box>
  );
}
